/**
 * usePackPurchase — fluxo de compra de pacote por PIX, compartilhado entre Integrações e /billing.
 *
 * Extraído VERBATIM do IntegrationsPage (plano 19, TODO 1.3): createOrder → 400 pede CPF →
 * PixQrCheckout. Nada de regra nova aqui — só passou a viver num lugar só para as duas telas
 * não divergirem (uma corrigindo o CPF e a outra não, por exemplo).
 *
 * Uso:
 *   const compra = usePackPurchase({ onPaid, showError, showSuccess });
 *   <PackCpfDialog {...compra.cpfDialog} />
 *   {compra.checkoutOrder && <PixQrCheckout order={compra.checkoutOrder} onClose={compra.closeCheckout} />}
 */
import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import billingService from '../services/billingService';

export default function usePackPurchase({ onPaid, showError, showSuccess } = {}) {
  const queryClient = useQueryClient();
  const [checkoutOrder, setCheckoutOrder] = useState(null);
  const [cpfFor, setCpfFor] = useState(null);
  const [cpf, setCpf] = useState('');
  const [cpfSubmitting, setCpfSubmitting] = useState(false);
  const [cpfError, setCpfError] = useState(null);

  // Sem providerSlug: escolher adquirente é do Billing (ele vê taxa de sucesso e quem caiu).
  // Mandar o slug daqui vazava o adquirente para o front e impedia o Billing de desviar.
  const startPurchase = useCallback((product, document) => {
    const input = { kind: 'pack', productId: product.id, methods: ['Pix'] };
    if (document) input.document = document;
    return billingService.createOrder(input);
  }, []);

  // A API é quem garante "uma compra por pacote": abrir de novo devolve o MESMO PIX (reused) e, se o
  // pagamento já entrou — inclusive por outra máquina —, responde alreadyPaid em vez de um QR novo.
  // A tela nunca decide isso sozinha (17/09/2026: dois PIX do mesmo pacote, um pago e outro pagável).
  const openCheckout = useCallback((order) => {
    if (order?.alreadyPaid) {
      queryClient.invalidateQueries({ queryKey: ['entitlements'] });
      onPaid?.();
      showSuccess?.('Este pacote já foi pago. Os créditos estão na sua conta.');
      return;
    }
    setCheckoutOrder(order);
  }, [queryClient, onPaid, showSuccess]);

  const handleBuy = useCallback(async (product) => {
    try {
      openCheckout(await startPurchase(product));
    } catch (err) {
      if (err?.response?.status === 400) { setCpf(''); setCpfError(null); setCpfFor(product); }
      else { showError?.('Não foi possível iniciar o pagamento. Tente novamente.'); }
    }
  }, [startPurchase, openCheckout, showError]);

  const handleCpfSubmit = useCallback(async () => {
    const digits = cpf.replace(/\D/g, '');
    if (digits.length !== 11) { setCpfError('Digite um CPF válido (11 dígitos).'); return; }
    setCpfSubmitting(true); setCpfError(null);
    try {
      const order = await startPurchase(cpfFor, digits);
      setCpfFor(null);
      openCheckout(order);
    } catch (err) {
      setCpfError(err?.response?.data?.message || 'Não foi possível iniciar o pagamento. Verifique o CPF e tente novamente.');
    } finally { setCpfSubmitting(false); }
  }, [cpf, cpfFor, startPurchase, openCheckout]);

  // Ao fechar o checkout, quem chamou recarrega o que precisa (o pagamento pode ter concedido lote novo).
  const closeCheckout = useCallback(() => { setCheckoutOrder(null); onPaid?.(); }, [onPaid]);

  return {
    checkoutOrder,
    closeCheckout,
    handleBuy,
    cpfDialog: {
      open: !!cpfFor,
      cpf, setCpf, cpfError, cpfSubmitting,
      onCancel: () => { if (!cpfSubmitting) setCpfFor(null); },
      onSubmit: handleCpfSubmit,
    },
  };
}
